import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  AlarmClock, ArrowRight, BadgeIndianRupee, Briefcase, CheckCircle2, FileText, Percent, Target, TrendingUp, Wallet,
} from 'lucide-react';
import { dateTimeLabel } from '@adisys/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Kpi, KpiSkeleton, inr } from '../../lib/crm';
import { PageHeader } from '../../components/AppShell';
import { Card, CardHeader, EmptyState, ErrorState, Skeleton, cx } from '../../components/ui';
import { AXIS_PROPS, BAR_RADIUS, CHART, ChartTooltip, GRID_PROPS } from '../../components/charts';

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
};

const TONE_DOT: Record<string, string> = { danger: 'bg-danger', warning: 'bg-warning', info: 'bg-info' };

export function CrmDashboardPage() {
  const { user, can } = useAuth();
  const q = useQuery({ queryKey: ['crm', 'dashboard'], queryFn: () => api.get('/crm/dashboard'), refetchInterval: 120_000 });
  const d = q.data;
  const k = d?.kpis ?? {};
  const has = (key: string) => k[key] !== undefined && k[key] !== null;
  const management = can('crm.stage.override');
  const firstName = user?.fullName.split(' ')[0] ?? '';

  const description = management
    ? 'The whole business: pipeline, revenue, approvals and what is stuck.'
    : can('crm.finance.manage') ? 'Goods to dispatch, invoices to raise and payments to chase.'
    : can('crm.procurement.manage') ? 'Stock to secure, purchase orders to place and deliveries to receive.'
    : can('crm.pricing.manage') ? 'BOQs waiting for pricing and discounts that need a decision.'
    : can('crm.boq.manage') ? 'Surveys to review and BOQs to prepare.'
    : 'Your enquiries, follow-ups and quotations.';

  return (
    <>
      <PageHeader title={`Good ${greeting()}, ${firstName}`} description={description} />
      <div className="space-y-5 p-4 sm:p-6">
        {q.isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <KpiSkeleton key={i} />)}</div>
        ) : q.isError ? (
          <Card><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>
        ) : d && (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {has('openLeads') && (can('crm.lead.view.own', 'crm.lead.view.all')) && (
                <Kpi label="Open enquiries" value={String(k.openLeads)} icon={Target} to="/crm/leads"
                  sub={`${k.qualifiedLeads} qualified · ${inr(k.openLeadValue, true)} stated budget`} />
              )}
              {has('overdueFollowups') && can('crm.lead.view.own', 'crm.lead.view.all') && (
                <Kpi label="Overdue follow-ups" value={String(k.overdueFollowups)} icon={AlarmClock}
                  tone={k.overdueFollowups > 0 ? 'danger' : 'neutral'} to="/crm/leads?followUp=overdue"
                  sub={k.overdueFollowups > 0 ? 'Customers waiting for a call back' : 'All caught up'} />
              )}
              {has('activeProjects') && (
                <Kpi label="Active projects" value={String(k.activeProjects)} icon={Briefcase} to="/crm/projects"
                  sub={`${inr(k.pipelineValue, true)} in the pipeline`} />
              )}
              {has('quotationsOut') && (
                <Kpi label="Quotations with customers" value={String(k.quotationsOut)} icon={FileText}
                  sub={`${inr(k.quotationsOutValue, true)} awaiting a decision`} />
              )}
              {has('wonLast30') && (
                <Kpi label="Won, last 30 days" value={String(k.wonLast30)} icon={CheckCircle2} tone="success"
                  sub={`${inr(k.wonValueLast30, true)}${k.winRate30 !== null && k.winRate30 !== undefined ? ` · ${k.winRate30}% win rate` : ''}`} />
              )}
              {has('billedThisMonth') && (
                <Kpi label="Billed this month" value={inr(k.billedThisMonth, true)} icon={BadgeIndianRupee}
                  sub={`${inr(k.collectedThisMonth, true)} collected`} to="/crm/finance" />
              )}
              {has('outstanding') && (
                <Kpi label="Outstanding" value={inr(k.outstanding, true)} icon={Wallet}
                  tone={k.overdue > 0 ? 'warning' : 'neutral'} to="/crm/finance"
                  sub={k.overdue > 0 ? `${inr(k.overdue, true)} overdue · ${k.overdueCount} invoices` : 'Nothing overdue'} />
              )}
              {has('marginPct90') && (
                <Kpi label="Margin, last 90 days" value={`${k.marginPct90}%`} icon={Percent} tone="success"
                  sub={`${inr(k.marginAmount90, true)} on approved pricing`} />
              )}
            </div>

            <div className="grid gap-5 xl:grid-cols-3">
              <Card className="xl:col-span-2">
                <CardHeader title="Pipeline by stage" subtitle="Projects in flight and their value. Select a stage to see them."
                  action={<Link to="/crm/projects" className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:underline">Open board <ArrowRight className="h-3.5 w-3.5" /></Link>} />
                <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
                  {d.stages.map((s: any) => (
                    <li key={s.stage}>
                      <Link to={`/crm/projects?stage=${s.stage}`}
                        className={cx('block rounded-lg p-3 ring-1 ring-inset transition-colors hover:bg-ink-50',
                          s.count ? 'ring-line-strong' : 'ring-line')}>
                        <p className="text-[12px] font-medium leading-tight text-ink-500">{s.label}</p>
                        <p className="tabular mt-1.5 text-xl font-semibold text-ink-900">{s.count}</p>
                        <p className="tabular text-xs text-ink-500">{inr(s.value, true)}</p>
                      </Link>
                    </li>
                  ))}
                </ol>
              </Card>

              <Card>
                <CardHeader title="Needs your attention" subtitle="Work waiting on you, most urgent first." />
                {d.queue.length === 0 ? (
                  <EmptyState icon={<CheckCircle2 className="h-5 w-5" />} title="Nothing waiting" description="You are clear for now." />
                ) : (
                  <ul className="-my-1 divide-y divide-line">
                    {d.queue.map((it: any, i: number) => (
                      <li key={i}>
                        <Link to={it.link} className="flex items-start gap-3 rounded-lg px-1 py-2.5 hover:bg-ink-50">
                          <span className={cx('mt-1.5 h-2 w-2 shrink-0 rounded-full', TONE_DOT[it.tone ?? 'info'] ?? 'bg-info')} aria-hidden />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-ink-900">{it.title}</span>
                            <span className="block truncate text-xs text-ink-500">{it.subtitle}{it.at ? ` · ${dateTimeLabel(it.at)}` : ''}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>

            {d.revenueTrend.length > 0 && (
              <Card>
                <CardHeader title="Billed and collected" subtitle="Last six months, including GST."
                  action={<TrendingUp className="h-4 w-4 text-ink-400" aria-hidden />} />
                <div className="h-64" role="img" aria-label="Billed and collected by month">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={d.revenueTrend} margin={{ left: 0, right: 8 }}>
                      <CartesianGrid {...GRID_PROPS} />
                      <XAxis dataKey="label" {...AXIS_PROPS} />
                      <YAxis {...AXIS_PROPS} width={56} tickFormatter={(v) => inr(v, true)} />
                      <Tooltip content={<ChartTooltip formatter={(v: number) => inr(v)} />} />
                      <Bar dataKey="billed" name="Billed" fill={CHART.assigned} radius={BAR_RADIUS} />
                      <Bar dataKey="collected" name="Collected" fill={CHART.approved} radius={BAR_RADIUS} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="mt-2 flex gap-4 text-xs text-ink-500">
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: CHART.assigned }} />Billed</span>
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: CHART.approved }} />Collected</span>
                </p>
              </Card>
            )}

            <div className="grid gap-5 xl:grid-cols-2">
              <Card>
                <CardHeader title="Recent activity" subtitle="Latest movement across the projects you can see." />
                {d.activity.length === 0 ? (
                  <EmptyState title="No activity yet" description="Steps taken on projects will appear here." />
                ) : (
                  <ul className="space-y-3">
                    {d.activity.map((a: any) => (
                      <li key={a.id} className="flex gap-3">
                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-400" aria-hidden />
                        <div className="min-w-0 text-sm">
                          <p className="text-ink-800">{a.summary}</p>
                          <p className="text-xs text-ink-500">
                            <Link to={`/crm/projects/${a.projectId}`} className="hover:underline">{a.projectCode}</Link>
                            {' · '}{a.actorName ?? 'System'} · {dateTimeLabel(a.createdAt)}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              {d.team && (
                <Card padded={false}>
                  <div className="p-5 pb-0"><CardHeader title="Sales team" subtitle="Enquiries handled and orders won." /></div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[420px] text-left text-sm">
                      <thead><tr className="border-y border-line bg-sunken/60 text-xs text-ink-600">
                        <th className="px-4 py-2 font-bold">Executive</th>
                        <th className="px-3 py-2 text-right font-bold">Leads</th>
                        <th className="px-3 py-2 text-right font-bold">Converted</th>
                        <th className="px-3 py-2 text-right font-bold">Active</th>
                        <th className="px-4 py-2 text-right font-bold">Won value</th>
                      </tr></thead>
                      <tbody className="divide-y divide-line">
                        {d.team.map((t: any) => (
                          <tr key={t.id}>
                            <td className="px-4 py-2.5 font-medium text-ink-900">{t.fullName}</td>
                            <td className="tabular px-3 py-2.5 text-right">{t.leads}</td>
                            <td className="tabular px-3 py-2.5 text-right">{t.converted}</td>
                            <td className="tabular px-3 py-2.5 text-right">{t.activeProjects}</td>
                            <td className="tabular px-4 py-2.5 text-right font-medium">{inr(t.wonValue, true)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>
          </>
        )}
        {q.isLoading && <Skeleton className="h-40 w-full" />}
      </div>
    </>
  );
}
